let permissionRequested = false;
export const ensurePermission = async () => {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') return Notification.permission;
  if (permissionRequested) return Notification.permission;
  permissionRequested = true;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
};

// Notifications go through the service worker so they can carry action
// buttons; the plain Notification constructor cannot render them. If the
// worker isn't ready (unsupported browser, failed registration) we fall
// back to a plain, button-less notification.
const SW_READY_TIMEOUT_MS = 1500;
const swRegistration = async () => {
  if (!('serviceWorker' in navigator)) return null;
  const timeout = new Promise((resolve) => setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS));
  try {
    return await Promise.race([navigator.serviceWorker.ready, timeout]);
  } catch {
    return null;
  }
};

const fallbackCallNotifications = new Map(); // callId -> Notification
const callActionListeners = new Set();
const messageClickListeners = new Set();

const emitCallAction = (data) => {
  for (const fn of callActionListeners) {
    try { fn(data); } catch (e) { console.error(e); }
  }
};

const emitMessageClick = (sessionId) => {
  for (const fn of messageClickListeners) {
    try { fn(sessionId); } catch (e) { console.error(e); }
  }
};

if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data || {};
    if (data.type === 'call-notification-action') {
      emitCallAction({ notificationAction: data.notificationAction, callId: data.callId });
    } else if (data.type === 'message-notification-click') {
      emitMessageClick(data.sessionId);
    }
  });
}

export const showCallNotification = async (callData) => {
  // The in-app incoming-call modal covers the focused case.
  if (document.hasFocus()) return;
  const perm = await ensurePermission();
  if (perm !== 'granted') return;

  const title = callData.displayName ? `Incoming Call from ${callData.displayName}` : 'Incoming Call';
  const registration = await swRegistration();
  if (registration) {
    await registration.showNotification(title, {
      body: callData.number || '',
      icon: '/icons/icon-192.png',
      tag: `call:${callData.id}`,
      requireInteraction: true,
      actions: [
        { action: 'accept', title: 'Answer' },
        { action: 'reject', title: 'Reject' },
      ],
      data: { kind: 'call', callId: callData.id },
    });
    return;
  }

  try {
    const n = new Notification(title, {
      body: callData.number || '',
      tag: `call:${callData.id}`,
      requireInteraction: true,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
    n.onclose = () => fallbackCallNotifications.delete(callData.id);
    fallbackCallNotifications.set(callData.id, n);
  } catch {
    // Notification constructor can throw (e.g. Android Chrome); nothing to do.
  }
};

export const hideCallNotification = async (callId) => {
  const fallback = fallbackCallNotifications.get(callId);
  if (fallback) {
    fallback.close();
    fallbackCallNotifications.delete(callId);
  }
  const registration = await swRegistration();
  if (!registration) return;
  // showNotification resolves before the notification is queryable, so a
  // call that ends right after it rings could otherwise leave an orphaned
  // notification. Retry once to cover that window.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const notifications = await registration.getNotifications({ tag: `call:${callId}` });
    if (notifications.length > 0) {
      notifications.forEach((n) => n.close());
      return;
    }
    if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 800));
  }
};

export const showMessageNotification = async ({ title, body, sessionId }) => {
  // The in-app snackbar covers the focused case.
  if (document.hasFocus()) return;
  const perm = await ensurePermission();
  if (perm !== 'granted') return;

  const registration = await swRegistration();
  if (registration) {
    await registration.showNotification(title, {
      body,
      icon: '/icons/icon-192.png',
      tag: `msg:${sessionId}`,
      data: { kind: 'message', sessionId },
    });
    return;
  }

  try {
    const n = new Notification(title, { body, tag: `msg:${sessionId}` });
    n.onclick = () => {
      window.focus();
      emitMessageClick(sessionId);
      n.close();
    };
  } catch {
    // Notification constructor can throw; nothing to do.
  }
};

export const onCallNotificationAction = (callback) => {
  callActionListeners.add(callback);
};

export const removeCallNotificationAction = () => {
  callActionListeners.clear();
};

export const onMessageNotificationClick = (callback) => {
  messageClickListeners.add(callback);
  return () => messageClickListeners.delete(callback);
};

export const setUnreadMessageBadge = (count) => {
  if (typeof navigator === 'undefined') return;
  if (count > 0 && navigator.setAppBadge) navigator.setAppBadge(count).catch(() => {});
  else if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
};

export const onRestoreNotificationBadge = (callback) => {
  const handler = () => {
    if (document.visibilityState === 'visible') callback();
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
};
